import { inject } from '@angular/core';

import * as signalR from '@microsoft/signalr';
import { OAuthService } from 'angular-oauth2-oidc';
import { BehaviorSubject, firstValueFrom } from 'rxjs';

import { NexRefreshAuthTokenService } from '@nexweb/shared/refresh-token-services';
import { API_URL_TOKEN } from '@nexweb/shared/tokens/api-url';

import { SignalRHubNames } from '../enums';

const HUBSROUTE = '/signalr-hubs/';

export abstract class BaseSignalR {
  protected oAuthService = inject(OAuthService);
  private apiUrl = inject(API_URL_TOKEN);
  private nexRefreshAuthTokenService = inject(NexRefreshAuthTokenService);
  private _hubConnection: signalR.HubConnection | undefined;
  private _hubConnectionEstablished$ = new BehaviorSubject<boolean>(false);
  private readonly hubName: string;

  protected constructor(hubName: SignalRHubNames) {
    this.hubName = hubName;
  }

  public get hubConnection(): signalR.HubConnection | undefined {
    return this._hubConnection;
  }

  public get hubConnectionEstablished$(): BehaviorSubject<boolean> {
    return this._hubConnectionEstablished$;
  }

  /**
   * Initialize the SignalR connection: stop any existing connection, build a new one, start it, and register events.
   * Override in subclasses that need additional setup before or after initialization.
   */
  public initialize(): void {
    this.stopConnection();
    this.buildConnection(this.hubName);
    this.startConnection().then();
    this.initEvents();
  }

  /**
   * Build the SignalR connection using provided parameters
   * @param {string} hubName - The name of the hub
   */
  public buildConnection(hubName: string): void {
    const hubUrl = `${this.apiUrl}${HUBSROUTE}${hubName}`;

    this._hubConnection = new signalR.HubConnectionBuilder()
      .withUrl(hubUrl, {
        accessTokenFactory: async () => {
          if (!this.oAuthService.hasValidAccessToken()) {
            await firstValueFrom(this.nexRefreshAuthTokenService.ensureValidAccessToken$());
          }

          return this.nexRefreshAuthTokenService.getAccessToken();
        },
        headers: {
          // eslint-disable-next-line @typescript-eslint/naming-convention
          'X-Requested-With': 'XMLHttpRequest',
        },
      })
      .withAutomaticReconnect({
        nextRetryDelayInMilliseconds: (retryContext) => {
          // Stop retrying if the server rejected the token — retrying won't help if the
          // session is truly expired and the accessTokenFactory's refresh also failed
          if (retryContext.retryReason instanceof signalR.HttpError && retryContext.retryReason.statusCode === 401) {
            return null;
          }

          // Default backoff: 0, 2, 10, 30 seconds
          return [0, 2000, 10000, 30000][retryContext.previousRetryCount] ?? null;
        },
      })
      .build();

    this._hubConnection.onreconnecting(() => this._hubConnectionEstablished$.next(false));
    this._hubConnection.onreconnected(() => this._hubConnectionEstablished$.next(true));
    this._hubConnection.onclose(() => this._hubConnectionEstablished$.next(false));
  }

  /**
   * Start the SignalR connection
   */
  startConnection = async () => {
    try {
      await this._hubConnection?.start();
      this._hubConnectionEstablished$.next(true);
    } catch (err) {
      this._hubConnectionEstablished$.next(false);
      if (err instanceof signalR.HttpError && err.statusCode === 401) {
        const refreshed = await firstValueFrom(this.nexRefreshAuthTokenService.ensureValidAccessToken$());
        if (refreshed) {
          try {
            await this._hubConnection?.start();
            this._hubConnectionEstablished$.next(true);
          } catch (retryErr) {
            console.error('SignalR reconnect after token refresh failed:', retryErr);
          }
        } else {
          console.error('SignalR token refresh failed — unable to reconnect');
        }
      } else {
        console.error('SignalR connection error:', err);
      }
    }
  };

  /**
   * Stop the SignalR connection
   */
  public stopConnection(): void {
    if (this._hubConnection) {
      this._hubConnection.stop().then();
      this._hubConnection = undefined;
    }
  }

  /**
   * Initialize events that will be received from the hub on the SignalR connection
   */
  protected abstract initEvents(): void;
}
